import AsyncStorage from '@react-native-async-storage/async-storage';
import axios from 'axios';
import { postData } from './api';
import * as FileSystem from 'expo-file-system';
import * as Sharing from 'expo-sharing';

const LMS_API_BASE_URL = 'https://api.intuitlms.online/api';
const LMS_PROGRESS_KEY = 'lms_progress_data';
const LMS_TRANSCRIPTS_KEY = 'lms_transcripts';
const LMS_LAST_SYNC_KEY = 'lms_last_sync';
const LMS_AUTH_TOKEN_KEY = 'lms_auth_token';
const LMS_USER_ID_KEY = 'lms_user_id';
const LMS_PORTAL_LOGOUT_NEEDED = 'lms_portal_logout_needed';

// ─── HELPERS ─────────────────────────────────────────────────────────────────

/**
 * Configure a dedicated axios instance for the LMS API.
 */
const createLMSApi = async () => {
  const token = await AsyncStorage.getItem(LMS_AUTH_TOKEN_KEY);
  return axios.create({
    baseURL: LMS_API_BASE_URL,
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { 'Authorization': `Bearer ${token}` } : {})
    },
    timeout: 15000,
  });
};

/**
 * Save LMS credentials captured from the WebView.
 */
export const saveLMSAuth = async (token, userId) => {
  try {
    await AsyncStorage.setItem(LMS_AUTH_TOKEN_KEY, token);
    await AsyncStorage.setItem(LMS_USER_ID_KEY, userId.toString());
    console.log('[LMSService] LMS Auth saved successfully');
    return true;
  } catch (e) {
    console.error('[LMSService] Failed to save LMS auth:', e);
    return false;
  }
};

/**
 * Get the current LMS user ID.
 */
export const getLMSUserId = async () => {
  return await AsyncStorage.getItem(LMS_USER_ID_KEY);
};

/**
 * Check if we have an active LMS session.
 */
export const hasLMSAuth = async () => {
  const token = await AsyncStorage.getItem(LMS_AUTH_TOKEN_KEY);
  return !!token;
};

// ─── READ / WRITE PROGRESS ──────────────────────────────────────────────────

/**
 * Get LMS progress data from the backend.
 * Now refreshes from API but can fallback to cache.
 */
export const getLMSProgress = async (courseId = null) => {
  try {
    const lmsUserId = await getLMSUserId();
    if (!lmsUserId) return null;

    const lmsApi = await createLMSApi();

    // If a specific course is requested, use the self-paced progress API
    if (courseId) {
      const response = await lmsApi.get(`/self-paced/course/${courseId}/progress`);
      return response.data;
    }

    // Default: return the full summary which includes all courses
    const summary = await getLMSSummary();
    return summary;
  } catch (e) {
    console.error('[LMSService] getLMSProgress error:', e);
    // Fallback to local cache if offline
    const raw = await AsyncStorage.getItem(LMS_PROGRESS_KEY);
    return raw ? JSON.parse(raw) : null;
  }
};

/**
 * Get the timestamp of the last successful LMS data sync.
 */
export const getLastSyncTime = async () => {
  try {
    return await AsyncStorage.getItem(LMS_LAST_SYNC_KEY);
  } catch (e) {
    return null;
  }
};

/**
 * Clear all cached LMS data.
 */
export const clearLMSCache = async () => {
  await AsyncStorage.multiRemove([
    LMS_PROGRESS_KEY, 
    LMS_TRANSCRIPTS_KEY, 
    LMS_LAST_SYNC_KEY,
    LMS_AUTH_TOKEN_KEY,
    LMS_USER_ID_KEY
  ]);
  // Set flag that we need to force logout the portal next time a webview opens
  await AsyncStorage.setItem(LMS_PORTAL_LOGOUT_NEEDED, 'true');
};

/**
 * Check if we need to force a portal logout.
 */
export const isPortalLogoutNeeded = async () => {
  const needed = await AsyncStorage.getItem(LMS_PORTAL_LOGOUT_NEEDED);
  return needed === 'true';
};

/**
 * Clear the portal logout flag.
 */
export const clearPortalLogoutFlag = async () => {
  await AsyncStorage.removeItem(LMS_PORTAL_LOGOUT_NEEDED);
};

// ─── TRANSCRIPT DOWNLOADS ────────────────────────────────────────────────────

/**
 * Returns all locally saved transcript records.
 */
export const getDownloadedTranscripts = async () => {
  try {
    const raw = await AsyncStorage.getItem(LMS_TRANSCRIPTS_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch (e) {
    return [];
  }
};

/**
 * Download a transcript from the REST API.
 */
export const downloadTranscript = async (courseParams = {}) => {
  try {
    const lmsUserId = await getLMSUserId();
    const lmsToken = await AsyncStorage.getItem(LMS_AUTH_TOKEN_KEY);
    if (!lmsUserId || !lmsToken) throw new Error('LMS Authentication required. Please log in to the portal.');

    const courseId = courseParams.courseId;
    const unitId = courseParams.unitId;
    const isUnit = courseParams.isUnit === true;
    const courseName = courseParams.courseName || (isUnit ? 'Unit Transcript' : 'Full Transcript');
    
    const prefix = isUnit ? 'unit-transcript' : 'transcript';
    const idForFilename = isUnit ? unitId : (courseId || 'all');
    const safeFilename = `${prefix}-${(courseName).replace(/\s+/g, '-').toLowerCase()}-${idForFilename}.pdf`;
    const localPath = `${FileSystem.documentDirectory}${safeFilename}`;

    // Use the explicit LMS API URL for PDF generation
    let downloadUrl;
    if (isUnit && unitId) {
      // Unit Specific Transcript
      downloadUrl = `${LMS_API_BASE_URL}/student-units/${unitId}/unit-transcript`;
    } else {
      // Course or Full Transcript
      downloadUrl = `${LMS_API_BASE_URL}/students/${lmsUserId}/transcript${courseId ? `?courseId=${courseId}` : ''}`;
    }
    
    console.log(`[LMSService] Downloading ${isUnit ? 'Unit' : 'Course'} transcript from: ${downloadUrl}`);

    const downloadResult = await FileSystem.downloadAsync(downloadUrl, localPath, {
      headers: {
        'Authorization': `Bearer ${lmsToken}`
      },
    });

    if (downloadResult.status !== 200) {
      throw new Error(`Download failed with status: ${downloadResult.status}`);
    }

    // Save record to AsyncStorage
    const existingList = await getDownloadedTranscripts();
    const record = {
      courseId: isUnit ? unitId : (courseId || 'all'), // Store the specific ID used for this download
      unitId,
      isUnit,
      courseName,
      localPath,
      filename: safeFilename,
      downloadedAt: new Date().toISOString(),
    };
    const updatedList = [record, ...existingList.filter(t => t.courseId !== courseId)];
    await AsyncStorage.setItem(LMS_TRANSCRIPTS_KEY, JSON.stringify(updatedList));

    // Share / open the file
    await _openTranscript(localPath);

    return { success: true, localPath };
  } catch (error) {
    console.error('[LMSService] downloadTranscript error:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Open an already-downloaded transcript.
 */
export const openTranscript = async (localPath) => {
  return _openTranscript(localPath);
};

const _openTranscript = async (localPath) => {
  try {
    const canShare = await Sharing.isAvailableAsync();
    if (canShare) {
      await Sharing.shareAsync(localPath, {
        mimeType: 'application/pdf',
        dialogTitle: 'Open Transcript',
        UTI: 'com.adobe.pdf',
      });
    }
  } catch (e) {
    console.error('[LMSService] openTranscript error:', e);
  }
};

// ─── CORE SUMMARY ────────────────────────────────────────────────────────────

/**
 * Get summarized stats from the backend.
 * Returns { enrolled, completed, certificates, courses }
 */
export const getLMSSummary = async () => {
  try {
    const lmsUserId = await getLMSUserId();
    if (!lmsUserId) return { enrolled: 0, completed: 0, certificates: 0, courses: [], requiresLogin: true };

    const lmsApi = await createLMSApi();
    console.log(`[LMSService] Fetching LMS summary from ${LMS_API_BASE_URL} for LMS User: ${lmsUserId}`);
    
    const [summaryRes, certsRes] = await Promise.all([
      lmsApi.get(`/students/${lmsUserId}/lms-summary`),
      lmsApi.get(`/students/${lmsUserId}/certificates`).catch(e => ({ data: [] }))
    ]);
    
    const data = {
      ...summaryRes.data,
      lmsUserId: lmsUserId,
      certificatesList: certsRes.data || []
    };

    // Robust Sync: Push this progress to the YC Backend for Admin Tracking
    try {
      await postData('/user/lms-sync', { progress: data });
      console.log('[LMSService] Enhanced LMS progress (with certs) synced to YC Backend');
    } catch (e) {
      console.warn('[LMSService] Failed to sync progress to YC Backend:', e.message);
    }

    // Cache it for offline use
    await AsyncStorage.setItem(LMS_PROGRESS_KEY, JSON.stringify(data));
    await AsyncStorage.setItem(LMS_LAST_SYNC_KEY, data.lastSync || new Date().toISOString());

    return { ...data, requiresLogin: false };
  } catch (error) {
    console.error('[LMSService] getLMSSummary error:', error.message || error);
    
    // Try to load from cache on failure
    const raw = await AsyncStorage.getItem(LMS_PROGRESS_KEY);
    if (raw) return { ...JSON.parse(raw), requiresLogin: false };
    
    return { enrolled: 0, completed: 0, certificates: 0, courses: [], requiresLogin: true };
  }
};



